import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import styled from 'styled-components';

import CardSkeleton from 'components/Intelligence/CardSkeleton';
import { parseErrrorMessage } from 'containers/utils';
import useMultiLanguageTranslation from 'hooks/useMultiLanguageTranslation';

import { useOrbitContext } from '../OrbitContext';
import { TOrbit } from '../i18n/TModules';
import { OrbitBodyText, OrbitTextButton, orbitTokens } from '../styles';
import { scrollOrbitThreadToEnd } from '../threadScroll';
import { OrbitTurn } from '../types';
import useOrbitThreadFollow from '../useOrbitThreadFollow';
import OrbitHandoffCard from './OrbitHandoffCard';
import OrbitSysNote from './OrbitSysNote';
import OrbitTurnCard, { OrbitTurnCardVariant } from './OrbitTurnCard';

// The panel's div#chat 6034:4796 gaps every block by 12, the same as inside a turn. The page's
// blocks sit 13.5 apart (6265:4222 ends at y 475.47, 6265:4246 starts at 489).
const Root = styled.div<{ $page: boolean }>`
  > * + * {
    margin-top: ${({ $page }) => ($page ? 13.5 : 12)}px;
  }
`;

const Notice = styled(OrbitBodyText)`
  color: ${orbitTokens.textSecondary};
`;

const ErrorRow = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;

  /* Safari < 14.1 has no flex gap (conventions/components/cross-browser.md § 5). */
  > * + * {
    margin-left: 12px;
  }
`;

const ErrorText = styled(OrbitBodyText)`
  color: ${orbitTokens.negative};
`;

const LoadEarlierRow = styled.div`
  display: flex;
  justify-content: center;
`;

// Zero-height and excluded from the `> * + *` rhythm above, which would otherwise pad the bottom of
// the thread by the gap meant for the cards.
const ScrollSentinel = styled.div`
  height: 0;
  margin-top: 0 !important;
`;

// A question continues the investigation when it was asked in the same context as the one before
// it; the first turn of a session has nothing to continue.
const isContinuation = (turn: OrbitTurn, previous: OrbitTurn | undefined): boolean =>
  previous !== undefined &&
  turn.context.screen === previous.context.screen &&
  turn.context.customer_id === previous.context.customer_id;

const byCreatedAt = (first: OrbitTurn, second: OrbitTurn): number =>
  first.created_at.localeCompare(second.created_at) || first.id - second.id;

// The list is newest-first and revalidates after the polled turn settles, so for a moment the same
// turn arrives from both sources; the polled copy wins because it is the one that just changed.
const mergeTurns = (turns: OrbitTurn[], activeTurn: OrbitTurn | undefined): OrbitTurn[] => {
  const byId = new Map<number, OrbitTurn>();
  turns.forEach(turn => byId.set(turn.id, turn));
  if (activeTurn !== undefined) {
    byId.set(activeTurn.id, activeTurn);
  }
  return Array.from(byId.values()).sort(byCreatedAt);
};

export interface IOrbitMessageListProps {
  /** Which thread the turns are drawn for; see `OrbitTurnCard`'s `variant`. */
  variant?: OrbitTurnCardVariant;
}

const OrbitMessageList: React.FC<IOrbitMessageListProps> = ({ variant = 'panel' }) => {
  const { t } = useMultiLanguageTranslation();
  const {
    turns,
    activeTurn,
    activeTurnId,
    activeTurnError,
    pendingQuestion,
    isAsking,
    turnsLoading,
    turnsLoadingMore,
    hasOlderTurns,
    loadOlderTurns,
    turnsError,
    sessionStale,
    handoffContext,
    handoffAfterTurnId,
    revalidateTurns,
    ask,
    sessionId
  } = useOrbitContext();
  const rootRef = useRef<HTMLDivElement>(null);
  const endRef = useRef<HTMLDivElement>(null);

  const ordered = useMemo(() => mergeTurns(turns, activeTurn), [turns, activeTurn]);
  // Gated on the id, not on the fetched turn: `activeTurn` stays undefined for a poll that has not
  // answered yet, and the list can win that race — the card would then render twice, once from the
  // transcript and once optimistically.
  const optimisticQuestion =
    pendingQuestion !== null && activeTurnId === null ? pendingQuestion : null;
  const lastState = ordered.length > 0 ? ordered[ordered.length - 1].state : undefined;
  const lastTurnId = ordered.length > 0 ? ordered[ordered.length - 1].id : undefined;
  /*
    The card sits after the turn that was newest when the handoff happened, so the questions asked
    since appear below it (`5017:8217` draws two turns under the card). It falls back to the end of
    the thread when that turn is not loaded - an anchor older than the first loaded page, or a
    handoff taken before the session had a turn at all.
  */
  const handoffAnchorId =
    handoffAfterTurnId !== null && ordered.some(entry => entry.id === handoffAfterTurnId)
      ? handoffAfterTurnId
      : null;

  // The panel brings its end into view on every new turn, state change and pending question.
  useEffect(() => {
    if (variant === 'panel') {
      scrollOrbitThreadToEnd(endRef.current);
    }
  }, [variant, ordered.length, lastState, optimisticQuestion]);

  const handleRetry = useCallback((question: string) => ask(question), [ask]);
  const handleReload = useCallback(() => revalidateTurns(), [revalidateTurns]);

  const showSkeleton = turnsLoading && ordered.length === 0 && optimisticQuestion === null;
  const listError = turnsError ?? activeTurnError;
  const showError = listError !== undefined && listError !== null && !sessionStale;
  const empty =
    !sessionStale &&
    !showSkeleton &&
    !showError &&
    ordered.length === 0 &&
    optimisticQuestion === null;

  // The page keeps a reader who scrolled up where they are; see the hook.
  useOrbitThreadFollow({
    enabled: variant === 'page',
    mounted: !empty,
    endRef,
    contentRef: rootRef,
    sessionId,
    hasTurns: ordered.length > 0,
    sending: optimisticQuestion
  });

  if (empty) {
    return null;
  }

  return (
    <Root ref={rootRef} data-testid="orbit-message-list" $page={variant === 'page'}>
      {sessionStale ? (
        <Notice role="status" data-testid="orbit-message-list-stale">
          {t(`${TOrbit}.notice.sessionEnded`)}
        </Notice>
      ) : null}
      {showSkeleton ? <CardSkeleton shape="list" rows={3} /> : null}
      {showError ? (
        <ErrorRow role="alert" data-testid="orbit-message-list-error">
          <ErrorText as="span">
            {parseErrrorMessage(listError, t(`${TOrbit}.notice.loadFailed`))}
          </ErrorText>
          <OrbitTextButton
            type="button"
            data-testid="orbit-message-list-reload"
            onClick={handleReload}
          >
            {t(`${TOrbit}.actions.retry`)}
          </OrbitTextButton>
        </ErrorRow>
      ) : null}
      {hasOlderTurns ? (
        <LoadEarlierRow>
          <OrbitTextButton
            type="button"
            disabled={turnsLoadingMore}
            data-testid="orbit-message-list-load-earlier"
            onClick={loadOlderTurns}
          >
            {t(`${TOrbit}.actions.loadEarlier`)}
          </OrbitTextButton>
        </LoadEarlierRow>
      ) : null}
      {ordered.length > 0 && !hasOlderTurns ? <OrbitSysNote /> : null}
      {ordered.map((turn, index) => (
        <React.Fragment key={turn.id}>
          <OrbitTurnCard
            question={turn.question}
            state={turn.state}
            turn={turn}
            announce={turn.id === lastTurnId && optimisticQuestion === null}
            continuation={isContinuation(turn, ordered[index - 1])}
            showFollowUps={turn.id === lastTurnId && optimisticQuestion === null}
            retryDisabled={isAsking}
            onRetry={handleRetry}
            variant={variant}
          />
          {handoffContext !== null && turn.id === handoffAnchorId ? (
            <OrbitHandoffCard context={handoffContext} />
          ) : null}
        </React.Fragment>
      ))}
      {optimisticQuestion !== null ? (
        <OrbitTurnCard question={optimisticQuestion} state="queued" announce variant={variant} />
      ) : null}
      {handoffContext !== null && handoffAnchorId === null ? (
        <OrbitHandoffCard context={handoffContext} />
      ) : null}
      <ScrollSentinel ref={endRef} aria-hidden="true" />
    </Root>
  );
};

export default OrbitMessageList;
